import { Badge } from 'prosci-ui';

interface CriterionJudgement {
  criterion?: string;
  met?: boolean;
  quote?: string;
  why?: string;
  unsupported?: boolean;
}

interface GuidedResultDetailProps {
  details: Record<string, unknown>;
}

/**
 * @param {GuidedResultDetailProps} props - the scoring details to explain
 * @returns {JSX.Element} what the judge read and why it agreed or disagreed
 */
export function GuidedResultDetail({ details }: GuidedResultDetailProps) {
  const breakdown = (details.criteria_breakdown as CriterionJudgement[]) ?? [];
  const expected = details.expected_criteria_met as boolean | undefined;
  const actual = details.actual_criteria_met as boolean | undefined;
  const injected = details.injected_block as string | undefined;
  const hint = details.improvement_hint as string | undefined;
  const error = details.error as string | undefined;

  const verdict = (value: boolean | undefined) =>
    value === undefined ? '—' : value ? 'meets the standard' : 'does not meet it';

  if (error) {
    return (
      <div className="rounded-md bg-destructive-50 p-3 text-sm text-destructive">{error}</div>
    );
  }

  return (
    <div className="space-y-3 rounded-md bg-muted/50 p-3 text-sm">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
        <span className="font-mono text-xs text-muted-foreground">
          {details.scope_key as string}
        </span>
        <span>
          <span className="font-medium">Human: </span>
          {verdict(expected)}
        </span>
        <span>
          <span className="font-medium">Kaiya: </span>
          {verdict(actual)}
          {expected !== undefined && actual !== undefined && (
            <Badge variant={expected === actual ? 'success' : 'destructive'} tint className="ml-2">
              {expected === actual ? 'agreed' : 'disagreed'}
            </Badge>
          )}
        </span>
      </div>

      {breakdown.length > 0 && (
        <div>
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Criterion by criterion
          </div>
          <ul className="space-y-1.5">
            {breakdown.map((entry, index) => (
              <li key={index} className="flex items-start gap-2">
                <span className={entry.met ? 'text-success' : 'text-destructive'}>
                  {entry.met ? '✓' : '✕'}
                </span>
                <span>
                  <span className="font-medium">{entry.criterion}</span>
                  {entry.why ? <span className="text-muted-foreground"> — {entry.why}</span> : null}
                  {entry.quote ? (
                    <span className="ml-1 text-muted-foreground">
                      quoting &ldquo;{entry.quote}&rdquo;
                    </span>
                  ) : null}
                  {entry.unsupported ? (
                    <Badge variant="destructive" tint className="ml-2">
                      quote not in the draft
                    </Badge>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {hint && (
        <div>
          <span className="font-medium">Told the practitioner: </span>
          stronger if you added {hint}
        </div>
      )}

      {injected && (
        <details>
          <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            What the judge read
          </summary>
          <pre className="mt-2 overflow-x-auto whitespace-pre-wrap rounded bg-background p-2 font-mono text-xs">
            {injected}
          </pre>
        </details>
      )}
    </div>
  );
}
